import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { ComplianceScore } from "@/lib/api/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const complianceBadgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      tier: {
        pass: "border-transparent bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        advisory:
          "border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
        "critical-fail":
          "border-transparent bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
        unaudited:
          "border-transparent bg-gray-100 text-gray-600 dark:bg-gray-800/30 dark:text-gray-400 animate-pulse",
      },
    },
    defaultVariants: {
      tier: "unaudited",
    },
  }
);

const tierLabels: Record<string, string> = {
  pass: "Pass",
  advisory: "Advisory",
  "critical-fail": "Critical Fail",
};

type ComplianceBadgeBaseProps = VariantProps<typeof complianceBadgeVariants> & {
  score: ComplianceScore | null | undefined;
  showScore?: boolean;
};

type InteractiveComplianceBadgeProps = ComplianceBadgeBaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
    /** When true (default), renders as a focusable <button> with tooltip */
    interactive?: true;
  };

type NonInteractiveComplianceBadgeProps = ComplianceBadgeBaseProps &
  Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> & {
    /** When false, renders as a non-interactive <span> safe for nesting inside links */
    interactive: false;
  };

export type ComplianceBadgeProps =
  | InteractiveComplianceBadgeProps
  | NonInteractiveComplianceBadgeProps;

function DimensionBreakdown({ score }: { score: ComplianceScore }) {
  return (
    <div className="space-y-1.5 text-xs">
      <div className="font-medium">
        Score: {score.overall}/100 — {tierLabels[score.tier] ?? score.tier}
      </div>
      {Object.keys(score.dimensions).length > 0 && (
        <div className="space-y-0.5">
          {Object.entries(score.dimensions).map(([dim, val]) => (
            <div key={dim} className="flex justify-between gap-3">
              <span className="capitalize">{dim.replace(/([A-Z])/g, " $1").trim()}</span>
              <span className="tabular-nums">{val}</span>
            </div>
          ))}
        </div>
      )}
      {score.criticalCount > 0 && (
        <div className="pt-1 border-t border-white/20">
          <span className="font-medium text-red-300">
            {score.criticalCount} critical issue{score.criticalCount !== 1 ? "s" : ""}
          </span>
        </div>
      )}
      {score.advisoryCount > 0 && (
        <div>
          <span className="font-medium text-yellow-300">
            {score.advisoryCount} advisory issue{score.advisoryCount !== 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
}

function ComplianceBadge({
  score,
  showScore = false,
  interactive = true,
  tier: _tierProp,
  className,
  ...props
}: ComplianceBadgeProps) {
  const tier = score?.tier ?? "unaudited";
  const label = score ? (tierLabels[score.tier] ?? score.tier) : "Unaudited";
  const displayText = showScore && score ? `${score.overall} — ${label}` : label;

  const badgeClassName = cn(complianceBadgeVariants({ tier: tier === "unaudited" ? "unaudited" : tier }), className);

  const badge = interactive ? (
    <button type="button" className={badgeClassName} {...props}>
      {displayText}
    </button>
  ) : (
    <span className={badgeClassName} {...props}>
      {displayText}
    </span>
  );

  if (!score) return badge;

  // Skip tooltip for non-interactive badges (no focusable target for keyboard users)
  if (!interactive) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <DimensionBreakdown score={score} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export { ComplianceBadge, complianceBadgeVariants };
